/**
 * MyUploadsDialog — lists all documents the employee has sent to HR.
 * Replaces the "uploads" tab that used to live inside EmployeeProfileDialog.
 *
 * The user can also delete uploads that haven't been read by HR yet (status="new").
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Inbox, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "../lib/documents";

export const MyUploadsDialog = ({ open, onClose }) => {
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/employee-uploads/mine")
            .then((r) => setUploads(r.data || []))
            .catch(() => setUploads([]))
            .finally(() => setLoading(false));
    }, [open]);

    const handleDelete = async (id) => {
        if (!window.confirm("Eliminare questo documento?")) return;
        try {
            await api.delete(`/employee-uploads/${id}`);
            setUploads((arr) => arr.filter((u) => u.id !== id));
            toast.success("Documento eliminato");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="my-uploads-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <Inbox className="h-5 w-5 text-[#376DB3]" />
                        I miei invii
                    </DialogTitle>
                    <DialogDescription>
                        Documenti che hai inviato all'ufficio HR. Puoi eliminare i documenti finché non sono stati letti.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2" data-testid="uploads-list">
                    {loading ? (
                        <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
                    ) : uploads.length === 0 ? (
                        <p className="py-6 text-center text-sm text-slate-500">Non hai ancora inviato documenti.</p>
                    ) : (
                        uploads.map((u) => (
                            <div key={u.id} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid={`my-upload-${u.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-slate-800">{u.title}</div>
                                        <div className="truncate text-xs text-slate-500">{u.filename}</div>
                                        <div className="mt-1 text-[10px] text-slate-400">
                                            {formatDateTime(u.uploaded_at)} ·{" "}
                                            {u.status === "read" ? (
                                                <span className="font-medium text-emerald-600">Letto dall'azienda</span>
                                            ) : (
                                                <span className="font-medium text-[#376DB3]">Da leggere</span>
                                            )}
                                        </div>
                                    </div>
                                    {u.status === "new" && (
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(u.id)}
                                            className="rounded p-1 text-red-500 hover:bg-red-50"
                                            title="Elimina"
                                            data-testid={`my-upload-delete-${u.id}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="my-uploads-close">Chiudi</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
